import { Dialog, DialogActions, DialogProps, useMediaQuery, useTheme } from '@mui/material';
import { memo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import styled from '@emotion/styled';
import { PmiDialogContent } from 'shell/components/shared/dialog/content';
import { useFormik } from 'formik';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { Grid } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/lab';
import { RecapVM } from 'civil-status/view-models/step-form';
import { useEventCallback } from '@mui/material';
import { ItemVM } from 'shell/view-models/item';
import ListAnsc from '../../../shared/list-ansc';

const regexPart = /^[12]/gi;
const regexSeries1 = /^[ABC]/gi;
const regexSeries2 = /^[123]/gi;

const validationSchema = yup.object({
  part: yup.string().nullable().notRequired().matches(regexPart, 'Il campo deve essere 1 o 2'),
  series1: yup
    .string()
    .nullable()
    .notRequired()
    .matches(regexSeries1, 'Il campo deve essere A, B o C'),
  series2: yup
    .string()
    .nullable()
    .notRequired()
    .matches(regexSeries2, 'Il campo deve essere 1, 2 o 3'),
  role: yup.string().required('Campo obbligatorio'),
});

const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

export interface AdvancedFormVM {
  date?: DateTime | null;
  time?: DateTime | null;
  series1?: string;
  series2?: string;
  part?: string;
  role?: string;
}

interface ModalAdvancedFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: AdvancedFormVM) => void;
  disabled?: boolean;
  dateFormationAct?: string;
  lastRecap?: RecapVM;
}

const ModalAdvancedFormComponent = ({
  open,
  onClose,
  onSave,
  disabled,
  dateFormationAct,
  lastRecap,
}: ModalAdvancedFormProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const formContext = useFormik<AdvancedFormVM>({
    initialValues: {
      date: dateFormationAct ? DateTime.fromISO(dateFormationAct) : DateTime.now(),
      time: dateFormationAct ? DateTime.fromISO(dateFormationAct) : DateTime.now(),
      series1: lastRecap?.serie1 || '',
      series2: lastRecap?.serie2 || '',
      part: lastRecap?.parte || '',
      role: lastRecap?.role || '2',
    },
    validationSchema: validationSchema,
    onSubmit: (values: AdvancedFormVM) => onSave(values),
  });

  const handleListRoleChange = useEventCallback((item?: ItemVM) => {
    formContext.setFieldValue('role', item?.id);
  });

  return (
    <PmiDialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        MODIFICA AVANZATA
      </PmiDialogTitle>
      <form>
        <PmiDialogContent style={{ padding: '30px 24px' }}>
          <Grid container spacing={2}>
            <Grid item container spacing={2}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Data formazione"
                  aria-label="Data formazione"
                  value={formContext.values?.date || null}
                  onChange={(date) => formContext.setFieldValue('date', date)}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField
                      {...params}
                      id="date"
                      fullWidth
                      helperText=""
                      // error={touched?.date && Boolean(errors?.date)}
                      // helperText={errors?.date}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TimePicker
                  label="Ora formazione"
                  aria-label="Ora formazione"
                  value={formContext.values?.time || null}
                  onChange={(date) => {
                    formContext.setFieldValue('time', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField
                      id="time"
                      {...params}
                      fullWidth
                      helperText=""
                      // error={touched?.time && Boolean(errors?.time)}
                      // helperText={errors?.time}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiTextField
                id="part"
                label="Parte"
                aria-label="Parte"
                fullWidth
                onChange={(event) => formContext.setFieldValue('part', event.target.value)}
                value={formContext.values.part}
                error={formContext.touched?.part && Boolean(formContext.errors?.part)}
                helperText={formContext.errors?.part}
                inputProps={{
                  maxLength: '1',
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiTextField
                id="series1"
                label="Serie 1"
                aria-label="Serie 1"
                fullWidth
                onChange={(event) => formContext.setFieldValue('series1', event.target.value)}
                value={formContext.values.series1}
                error={formContext.touched?.series1 && Boolean(formContext.errors?.series1)}
                helperText={formContext.errors?.series1}
                inputProps={{
                  maxLength: '1',
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <PmiTextField
                id="series2"
                label="Serie 2"
                aria-label="Serie 2"
                fullWidth
                onChange={(event) => formContext.setFieldValue('series2', event.target.value)}
                value={formContext.values.series2}
                error={formContext.touched?.series2 && Boolean(formContext.errors?.series2)}
                helperText={formContext.errors?.series2}
                inputProps={{
                  maxLength: '1',
                }}
              />
            </Grid>
            <Grid container item spacing={2} xs={12}>
              <Grid item xs={12} md={4}>
                <ListAnsc
                  label="Ruolo USC"
                  idDominio="63"
                  required
                  value={formContext.values.role || ''}
                  onChange={handleListRoleChange}
                  disabled={disabled}
                  error={Boolean(formContext.errors?.role) && formContext.touched?.role}
                />
              </Grid>
            </Grid>
          </Grid>
        </PmiDialogContent>
        <DialogActions>
          <PmiButton
            variant="outlined"
            color="cdmBrandThird"
            disabled={disabled}
            onClick={() => formContext.handleSubmit()}
          >
            Salva
          </PmiButton>
        </DialogActions>
      </form>
    </PmiDialog>
  );
};

ModalAdvancedFormComponent.displayName = 'Modal Advanced component';

const ModalAdvancedForm = memo(ModalAdvancedFormComponent);

export default ModalAdvancedForm;
